import { useEffect, useState, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { colors, formatNumber, formatPercent } from '../../styles/tokens'
import { Button, Input, Select } from '../../components/FormControls'
import { EmptyState, ErrorBanner, SuccessBanner, SkeletonRows } from '../../components/Feedback'
import EstimationBreadcrumb from '../../components/EstimationBreadcrumb'

export default function MarkupPage() {
  const { tenderId, revisionId } = useParams()
  const [settings, setSettings] = useState(null)
  const [packages, setPackages] = useState([])
  const [boqItems, setBoqItems] = useState([])
  const [isFrozen, setIsFrozen] = useState(false)
  const [error, setError] = useState(null)
  const [successMsg, setSuccessMsg] = useState(null)

  // form state
  const [scopeLevel, setScopeLevel] = useState('overall')
  const [scopeReferenceId, setScopeReferenceId] = useState('')
  const [markupPct, setMarkupPct] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const loadAll = useCallback(async () => {
    const revRes = await supabase.from('estimate_revision').select('status').eq('id', revisionId).maybeSingle()
    const frozen = revRes.data?.status === 'frozen'
    setIsFrozen(frozen)

    if (frozen) {
      // Frozen: markup_setting's raw Overall/Package/Line rules ARE
      // captured verbatim inside freeze_snapshot.full_indirect_data.markup
      // (confirmed directly from freeze_estimate_revision's actual body).
      // Read-only here -- editing stays disabled, same as every other
      // Frozen page, but the rules themselves are real historical data,
      // not fabricated.
      const snapRes = await supabase
        .from('freeze_snapshot')
        .select('full_indirect_data, full_boq_data')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
      if (snapRes.error) return setError(snapRes.error.message)
      const indirect = snapRes.data?.full_indirect_data && typeof snapRes.data.full_indirect_data === 'object' ? snapRes.data.full_indirect_data : {}
      const boqData = Array.isArray(snapRes.data?.full_boq_data) ? snapRes.data.full_boq_data : []
      const wbsRes = await supabase
        .from('freeze_snapshot')
        .select('full_wbs_snapshot_data')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
      const wbsNodes = Array.isArray(wbsRes.data?.full_wbs_snapshot_data) ? wbsRes.data.full_wbs_snapshot_data : []
      setSettings(Array.isArray(indirect.markup) ? indirect.markup : [])
      setPackages(wbsNodes.filter((n) => n.is_costing_package))
      setBoqItems(boqData.map((it) => ({ id: it.id, item_no: it.item_no, description: it.description })))
      return
    }

    const [settingsRes, packagesRes, boqRes] = await Promise.all([
      supabase.from('markup_setting').select('id, scope_level, scope_reference_id, markup_pct').eq('estimate_revision_id', revisionId),
      supabase.from('wbs_snapshot_node').select('id, name').eq('estimate_revision_id', revisionId).eq('is_costing_package', true),
      supabase.from('boq_item').select('id, item_no, description').eq('estimate_revision_id', revisionId).order('item_no'),
    ])
    if (settingsRes.error) return setError(settingsRes.error.message)
    if (packagesRes.error) return setError(packagesRes.error.message)
    if (boqRes.error) return setError(boqRes.error.message)

    setSettings(settingsRes.data)
    setPackages(packagesRes.data)
    setBoqItems(boqRes.data)
  }, [revisionId])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  async function handleSubmit(e) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    setSuccessMsg(null)

    const payload = {
      estimate_revision_id: revisionId,
      scope_level: scopeLevel,
      scope_reference_id: scopeLevel === 'overall' ? null : scopeReferenceId,
      markup_pct: Number(markupPct),
    }

    const { error } = await supabase.from('markup_setting').insert(payload)

    if (error) setError(error.message)
    else {
      setSuccessMsg('Markup setting saved.')
      setScopeReferenceId('')
      setMarkupPct('')
      await loadAll()
    }
    setSubmitting(false)
  }

  async function handleDelete(id) {
    setError(null)
    const { error } = await supabase.from('markup_setting').delete().eq('id', id)
    if (error) setError(error.message)
    else {
      setSuccessMsg('Markup setting removed.')
      await loadAll()
    }
  }

  function labelFor(setting) {
    if (setting.scope_level === 'overall') return 'Overall (Revision default)'
    if (setting.scope_level === 'package') {
      const pkg = packages.find((p) => p.id === setting.scope_reference_id)
      return `Package: ${pkg?.name || setting.scope_reference_id}`
    }
    const item = boqItems.find((b) => b.id === setting.scope_reference_id)
    return `Line: ${item ? `${item.item_no} — ${item.description}` : setting.scope_reference_id}`
  }

  const overallExists = settings?.some((s) => s.scope_level === 'overall')

  return (
    <div>
      <EstimationBreadcrumb />
      <h1 style={{ fontSize: 22, fontWeight: 700, color: colors.textPrimary, margin: '10px 0 4px' }}>Markup</h1>
      <p style={{ fontSize: 13, color: colors.textSecondary, margin: '0 0 20px' }}>
        Resolution hierarchy: <strong>Line</strong> overrides <strong>Package</strong> overrides <strong>Overall</strong>.
      </p>

      <ErrorBanner message={error} />
      <SuccessBanner message={successMsg} />

      {isFrozen && (
        <div style={{ padding: '10px 16px', background: '#F1F5F9', color: colors.textSecondary, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          🔒 This revision is frozen — showing the immutable historical markup rules and their resolved effect per BOQ item. Editing is disabled.
        </div>
      )}

      {!isFrozen && !overallExists && settings !== null && (
        <div style={{ padding: '10px 14px', borderRadius: 6, background: colors.warningBg, color: colors.warningText, fontSize: 13, marginBottom: 16 }}>
          ⚠ No Overall markup is set yet. BOQ items without a Package or Line override will show 0% markup.
        </div>
      )}

      {!isFrozen && (
      <form
        onSubmit={handleSubmit}
        style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', padding: 16, background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, marginBottom: 20 }}
      >
        <Field label="Scope">
          <Select value={scopeLevel} onChange={(e) => { setScopeLevel(e.target.value); setScopeReferenceId('') }} style={{ width: 140 }}>
            <option value="overall">Overall</option>
            <option value="package">Package</option>
            <option value="line">Line (BOQ Item)</option>
          </Select>
        </Field>

        {scopeLevel === 'package' && (
          <Field label="Package">
            <Select value={scopeReferenceId} onChange={(e) => setScopeReferenceId(e.target.value)} required style={{ width: 240 }}>
              <option value="" disabled>Select package…</option>
              {packages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
        )}

        {scopeLevel === 'line' && (
          <Field label="BOQ Item">
            <Select value={scopeReferenceId} onChange={(e) => setScopeReferenceId(e.target.value)} required style={{ width: 300 }}>
              <option value="" disabled>Select item…</option>
              {boqItems.map((b) => <option key={b.id} value={b.id}>{b.item_no} — {b.description}</option>)}
            </Select>
          </Field>
        )}

        <Field label="Markup %">
          <Input type="number" step="any" min="0" value={markupPct} onChange={(e) => setMarkupPct(e.target.value)} required style={{ width: 100 }} />
        </Field>

        <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : '+ Add Markup Rule'}</Button>
      </form>
      )}

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 24 }}>
        {settings === null && <div style={{ padding: 16 }}><SkeletonRows rows={2} cols={2} /></div>}
        {settings?.length === 0 && (
          <div style={{ padding: 24 }}>
            <EmptyState title="No markup rules yet" description="Add an Overall markup first, then override for specific packages or lines." />
          </div>
        )}
        {settings?.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                <Th>Scope</Th>
                <Th align="right">Markup %</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {settings.map((s) => (
                <tr key={s.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <Td>{labelFor(s)}</Td>
                  <Td align="right" strong>{formatPercent(s.markup_pct)}</Td>
                  <Td align="right">
                    {!isFrozen && <button onClick={() => handleDelete(s.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 13 }}>Remove</button>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <EffectiveMarkupTable revisionId={revisionId} boqItems={boqItems} isFrozen={isFrozen} />
    </div>
  )
}

// Shows the RESOLVED markup per BOQ item. Draft: read directly from the
// existing boq_item_effective_markup_pct view -- no resolution logic
// duplicated here. Frozen: the same resolved percentage is already
// captured verbatim inside freeze_snapshot.full_boq_data (each item's
// effective_markup_pct), read from there instead -- never recomputed.
function EffectiveMarkupTable({ revisionId, boqItems, isFrozen }) {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (isFrozen) {
      supabase
        .from('freeze_snapshot')
        .select('full_boq_data')
        .eq('estimate_revision_id', revisionId)
        .maybeSingle()
        .then(({ data, error }) => {
          if (error) return setError(error.message)
          const boqData = Array.isArray(data?.full_boq_data) ? data.full_boq_data : []
          setRows(boqData.map((it) => ({ item_no: it.item_no, description: it.description, effective_markup_pct: it.effective_markup_pct })))
        })
      return
    }
    supabase
      .from('boq_item_effective_markup_pct')
      .select('boq_item_id, effective_markup_pct')
      .eq('estimate_revision_id', revisionId)
      .then(({ data, error }) => {
        if (error) return setError(error.message)
        const effectiveById = Object.fromEntries(data.map((d) => [d.boq_item_id, d.effective_markup_pct]))
        setRows(boqItems.map((item) => ({ item_no: item.item_no, description: item.description, effective_markup_pct: effectiveById[item.id] })))
      })
  }, [revisionId, boqItems, isFrozen])

  return (
    <div>
      <h2 style={{ fontSize: 15, fontWeight: 700, color: colors.textPrimary, margin: '0 0 12px' }}>Effective Markup per BOQ Item</h2>
      <ErrorBanner message={error} />
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: 'hidden' }}>
        {rows === null && !error && <div style={{ padding: 16 }}><SkeletonRows rows={3} cols={2} /></div>}
        {rows && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: '#FAFBFC', borderBottom: `1px solid ${colors.border}` }}>
                <Th>Item</Th>
                <ThCalc align="right">Effective Markup %</ThCalc>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <Td>{row.item_no} — {row.description}</Td>
                  <TdCalc align="right" strong>{formatPercent(row.effective_markup_pct)}</TdCalc>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.3 }}>
      {label}
      {children}
    </label>
  )
}
function Th({ children, align = 'left' }) {
  return <th style={{ textAlign: align, padding: '10px 16px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>{children}</th>
}
function ThCalc({ children, align = 'left' }) {
  return <th style={{ textAlign: align, padding: '10px 16px', fontSize: 12, fontWeight: 600, color: colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4, background: '#F8FAFF' }}>{children}</th>
}
function Td({ children, align = 'left' }) {
  return <td style={{ textAlign: align, padding: '11px 16px', fontSize: 14, color: colors.textPrimary }}>{children}</td>
}
function TdCalc({ children, align = 'left', strong }) {
  return <td style={{ textAlign: align, padding: '11px 16px', fontSize: 14, color: strong ? colors.primary : colors.textSecondary, fontWeight: strong ? 700 : 400, background: '#FBFCFF' }}>{children}</td>
}
